import * as React from "react";
import { CheckmarkIcon, CloseIcon } from "vega-icons";

import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { ThemedIconButton } from "../Buttons/ThemedIconButton";

export type ComboboxProps<
  Value,
  Multiple extends boolean | undefined = false,
> = ComboboxPrimitive.Root.Props<Value, Multiple>;

const Combobox = ComboboxPrimitive.Root;

function ComboboxChip({
  className,
  children,
  ...props
}: ComboboxPrimitive.Chip.Props) {
  return (
    <ComboboxPrimitive.Chip
      className={cnTwMerge(
        "w-fit flex items-center justify-center gap-2 body-small-semibold border border-border/primary rounded-2xl py-0 pl-3 pr-1 bg-transparent",
        className,
      )}
      data-slot="combobox-chip"
      data-testid="combobox-chip"
      {...props}
    >
      {children}
      <ComboboxPrimitive.ChipRemove
        data-slot="combobox-chip-remove"
        render={({ role: _, ...props }) => (
          <ThemedIconButton
            {...props}
            className="rounded-full data-[disabled]:cursor-not-allowed data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
            dataTestId="combobox-chip-remove-button"
            icon={<CloseIcon className="text-text/secondary size-4.5" />}
            size="extraSmall"
            variant="noBackground"
          />
        )}
      />
    </ComboboxPrimitive.Chip>
  );
}

function ComboboxChips({
  className,
  hasError = false,
  disabled = false,
  ...props
}: ComboboxPrimitive.Chips.Props & {
  hasError?: boolean;
  disabled?: boolean;
}) {
  return (
    <ComboboxPrimitive.Chips
      className={cnTwMerge(
        "p-3 flex gap-2 border border-border/primary focus-within:border-ring focus-within:ring-0 focus-within:border-brand-default rounded-lg",
        hasError &&
          "border-border/negative focus-within:border-border/negative",
        disabled && "bg-background/component/10 cursor-not-allowed",
        className,
      )}
      data-slot="combobox-chips"
      {...props}
    />
  );
}

/**
 * Not from Base UI. Purely styling wrapper.
 */
function ComboboxChipsContent({
  className,
  ...props
}: React.ComponentPropsWithRef<"div">) {
  return (
    <div
      className={cnTwMerge("flex flex-wrap items-center gap-2 grow", className)}
      {...props}
    />
  );
}

function ComboboxChipsInput({
  className,
  disabled,
  ...props
}: ComboboxPrimitive.Input.Props) {
  return (
    <ComboboxPrimitive.Input
      className={cnTwMerge(
        // field-sizing lets long values wrap via parent flex-wrap instead of clipping.
        "min-w-16 flex-[1_0_auto] [field-sizing:content] outline-none border-none body-base-regular p-0 focus:ring-0 data-[disabled]:bg-transparent data-[disabled]:cursor-not-allowed",
        className,
      )}
      data-slot="combobox-chip-input"
      data-testid="combobox-chip-input"
      disabled={disabled}
      {...props}
    />
  );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
  return (
    <ComboboxPrimitive.Clear
      className={cnTwMerge(className)}
      data-slot="combobox-clear"
      {...props}
      render={
        <ThemedIconButton
          className="rounded-full"
          dataTestId="combobox-clear"
          icon={<CloseIcon className="text-text/secondary size-4.5" />}
          size="extraSmall"
          variant="noBackground"
        />
      }
    />
  );
}

function ComboboxContent({
  className,
  side = "bottom",
  sideOffset = 6,
  align = "start",
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<
    ComboboxPrimitive.Positioner.Props,
    "align" | "alignOffset" | "anchor" | "side" | "sideOffset"
  >) {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-tooltip"
        side={side}
        sideOffset={sideOffset}
      >
        <ComboboxPrimitive.Popup
          className={cnTwMerge(
            "border border-border/secondary bg-background/card shadow-lg overflow-hidden group/combobox-content rounded-lg relative max-h-(--available-height) w-(--anchor-width) max-w-(--available-width) min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) data-[chips=true]:min-w-(--anchor-width)",
            className,
          )}
          data-chips={!!anchor}
          data-slot="combobox-content"
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
  return (
    <ComboboxPrimitive.Empty
      className={cnTwMerge(
        "text-muted-foreground hidden w-full justify-center p-4 text-center text-sm group-data-[empty=]/combobox-content:flex",
        className,
      )}
      data-slot="combobox-empty"
      {...props}
    />
  );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
  return (
    <ComboboxPrimitive.Group
      className={cnTwMerge(className)}
      data-slot="combobox-group"
      {...props}
    />
  );
}

function ComboboxInput({ ...props }: ComboboxPrimitive.Input.Props) {
  return <ComboboxPrimitive.Input {...props} />;
}

function ComboboxItem({
  className,
  children,
  ...props
}: ComboboxPrimitive.Item.Props) {
  return (
    <ComboboxPrimitive.Item
      className={cnTwMerge(
        "rounded-md p-2 text-sm relative w-full flex gap-4 items-center justify-between cursor-pointer select-none data-[disabled]:opacity-50 hover:bg-background/tertiary/50",
        className,
      )}
      data-slot="combobox-item"
      {...props}
    >
      {children}
      <div className="size-6 shrink-0">
        <ComboboxPrimitive.ItemIndicator
          render={<CheckmarkIcon className="size-6" />}
        />
      </div>
    </ComboboxPrimitive.Item>
  );
}

function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
  return (
    <ComboboxPrimitive.List
      className={cnTwMerge(
        "max-h-72 p-2 overflow-y-auto overscroll-contain group-data-[empty=]/combobox-content:hidden",
        className,
      )}
      data-slot="combobox-list"
      {...props}
    />
  );
}

/**
 * Not from Base UI. Renders a left label for the combobox.
 */
function ComboboxLeftLabel({
  className,
  ...props
}: React.ComponentPropsWithRef<"label">) {
  return (
    <label
      className={cnTwMerge("body-base-regular pr-2 min-w-14", className)}
      data-testid="combobox-left-label"
      {...props}
    />
  );
}

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsContent,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLeftLabel,
  ComboboxList,
  ComboboxValue,
};
